import { useState } from "react";
import { toast } from "sonner";
import { Share2, Copy, X, Calendar, Trash2, FileDown } from "lucide-react";
import { api, folderZipUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";

export default function ShareFolderDialog({ folder, trigger }) {
  const [open, setOpen] = useState(false);
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);

  const shareUrl = share ? `${window.location.origin}/s/${share.token}` : "";

  const loadStatus = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/folders/${folder.id}/share`);
      if (r.data.active) setShare(r.data); else setShare(null);
    } catch (err) { console.warn("share status", err); }
    finally { setLoading(false); }
  };

  const createShare = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/folders/${folder.id}/share`, { days });
      setShare(r.data);
      toast.success("Link creato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setLoading(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Revocare il link? Il commercialista non potrà più accedere.")) return;
    setLoading(true);
    try {
      await api.delete(`/folders/${folder.id}/share`);
      setShare(null);
      toast.success("Link revocato");
    } catch { toast.error("Errore"); }
    finally { setLoading(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copiato negli appunti");
    } catch { toast.error("Impossibile copiare"); }
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) loadStatus();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display font-bold tracking-tight text-2xl">
            Condividi cartella
          </DialogTitle>
          <DialogDescription className="text-sm text-[#5C5C5C]">
            Crea un link protetto per condividere l'intera cartella con il commercialista.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <div className="mb-4 p-3 bg-[#F8F8F6] border border-black/10 rounded-xl">
            <div className="overline text-[#8A8A8A] mb-1">CARTELLA</div>
            <div className="font-medium">{folder.name}</div>
            <div className="text-xs text-[#5C5C5C] mt-0.5">{folder.document_count} documenti</div>
          </div>

          {share ? (
            <>
              <div className="text-xs text-[#5C5C5C] mb-2 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Attivo fino al {new Date(share.expires_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
              </div>
              <div className="flex gap-2 mb-4">
                <div className="flex-1 bg-black text-white text-xs font-mono-plex px-3 py-2.5 rounded-xl truncate">
                  {shareUrl}
                </div>
                <Button
                  data-testid="share-copy-btn"
                  onClick={copyLink}
                  className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white h-auto px-3"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-[#5C5C5C] leading-relaxed mb-4">
                Chi apre il link può vedere e scaricare tutti i documenti della cartella.
                Nessun account richiesto — perfetto da inviare al commercialista.
              </p>
              <div className="flex justify-between items-center">
                <button
                  data-testid="share-revoke-btn"
                  onClick={revoke}
                  disabled={loading}
                  className="text-xs text-[#FF3333] hover:underline flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Revoca link
                </button>
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-[#0A0A0A] hover:underline"
                  data-testid="share-preview-link"
                >
                  Apri anteprima →
                </a>
              </div>
              <div className="border-t border-black/5 pt-4 mt-4">
                <a
                  href={folderZipUrl(folder.id)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="share-zip-btn"
                  className="flex items-center justify-center gap-2 border border-black/15 hover:bg-black/5 rounded-xl h-10 text-sm font-medium transition-colors"
                >
                  <FileDown className="w-4 h-4" /> Scarica tutta la cartella (ZIP)
                </a>
                <div className="text-[10px] text-[#8A8A8A] mt-2 text-center">
                  Alternativa al link: invia il file zip direttamente al commercialista.
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-[#5C5C5C] mb-4 leading-relaxed">
                Crea un link protetto per condividere <b>tutti i documenti</b> di questa cartella con il commercialista.
                Nessun invio uno alla volta.
              </p>
              <div className="mb-4">
                <label className="overline text-[#8A8A8A] block mb-2">SCADENZA</label>
                <div className="flex gap-2">
                  {[7, 30, 90, 365].map(d => (
                    <button
                      key={d}
                      data-testid={`share-days-${d}`}
                      onClick={() => setDays(d)}
                      className={`px-3 py-2 text-xs rounded-xl border transition-colors ${
                        days === d ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-black/15 hover:bg-black/5"
                      }`}
                    >{d === 365 ? "1 anno" : `${d} giorni`}</button>
                  ))}
                </div>
              </div>
              <Button
                data-testid="share-create-btn"
                onClick={createShare}
                disabled={loading}
                className="w-full rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white h-11"
              >
                <Share2 className="w-4 h-4 mr-2" /> Genera link
              </Button>
              <div className="my-4 flex items-center gap-3">
                <div className="flex-1 h-px bg-black/10" />
                <span className="text-[10px] text-[#8A8A8A] uppercase tracking-widest">o</span>
                <div className="flex-1 h-px bg-black/10" />
              </div>
              <a
                href={folderZipUrl(folder.id)}
                target="_blank"
                rel="noreferrer"
                data-testid="share-zip-btn-inline"
                className="w-full flex items-center justify-center gap-2 border border-black/15 hover:bg-black/5 rounded-xl h-11 text-sm font-medium transition-colors"
              >
                <FileDown className="w-4 h-4" /> Scarica ZIP di tutta la cartella
              </a>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
